import { FormatListBulleted } from '@mui/icons-material';
import {
    Badge,
    Box,
    ListItemText,
    Menu,
    MenuItem,
    MenuList,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    ToggleButtonProps,
    Tooltip,
} from '@mui/material';
import React, { forwardRef, useState } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { useDeviceType } from '../../hooks/useDeviceType';
import { SearchFilter } from './filters';

interface TooltipToggleButtonProps extends ToggleButtonProps {
    tooltipKey?: MessageDescriptor['id'];
}

const TooltipToggleButton = forwardRef<HTMLButtonElement, TooltipToggleButtonProps>(({ tooltipKey, ...props }, ref) => {
    const button = <ToggleButton ref={ref} {...props} />;
    return tooltipKey ? <Tooltip title={<FormattedMessage id={tooltipKey} />}>{button}</Tooltip> : button;
});
TooltipToggleButton.displayName = 'TooltipToggleButton';

interface QuickFiltersButtonsProps {
    onPredefinedFilterClick: (predefinedFilter: SearchFilter[]) => void;
    predefinedFilters?: {
        filters: SearchFilter[];
        active: boolean;
        labelKey: MessageDescriptor['id'];
        tooltipKey?: MessageDescriptor['id'];
    }[];
}

export const QuickFiltersButtons: React.FC<QuickFiltersButtonsProps> = ({
    onPredefinedFilterClick,
    predefinedFilters,
}) => {
    const { isMobile } = useDeviceType();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const closeMenu = () => setAnchorEl(null);
    const withMenuClose =
        <T,>(f: () => T): (() => T) =>
        () => {
            closeMenu();
            return f();
        };

    return (
        <>
            <ToggleButtonGroup color="primary" size={isMobile ? undefined : 'small'}>
                {isMobile
                    ? !!predefinedFilters?.length && (
                          <ToggleButton
                              value="quickFilter"
                              onClick={(event) => setAnchorEl(event.currentTarget)}
                              selected={!!predefinedFilters?.filter((f) => f.active).length}
                          >
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <Badge
                                      badgeContent={predefinedFilters?.filter((f) => f.active).length}
                                      color="primary"
                                  >
                                      <FormatListBulleted />
                                  </Badge>
                                  {!isMobile && (
                                      <Box>
                                          <FormattedMessage id="search.quickFilters" />
                                      </Box>
                                  )}
                              </Stack>
                          </ToggleButton>
                      )
                    : predefinedFilters?.map((predefinedFilterData) => (
                          <TooltipToggleButton
                              key={predefinedFilterData.labelKey}
                              tooltipKey={predefinedFilterData.tooltipKey}
                              value={`quickFilter-${predefinedFilterData.labelKey}`}
                              selected={predefinedFilterData.active}
                              onClick={() => onPredefinedFilterClick(predefinedFilterData.filters)}
                          >
                              <FormattedMessage id={predefinedFilterData.labelKey} />
                          </TooltipToggleButton>
                      ))}
            </ToggleButtonGroup>
            <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                <MenuList dense>
                    {predefinedFilters?.map((predefinedFilterData) => (
                        <MenuItem
                            key={predefinedFilterData.labelKey}
                            selected={predefinedFilterData.active}
                            onClick={withMenuClose(() => onPredefinedFilterClick(predefinedFilterData.filters))}
                        >
                            <ListItemText>
                                <FormattedMessage id={predefinedFilterData.labelKey} />
                            </ListItemText>
                        </MenuItem>
                    ))}
                </MenuList>
            </Menu>
        </>
    );
};
